<template>
  <teleport to="body">
  <div :style="containerStyle">
    <a href="/" :style="backBtnStyle">&larr; Back</a>
    <canvas ref="canvasEl" :style="canvasStyle" />
    <div :style="titleStyle">Neural Plinko</div>

    <!-- View controls — left side -->
    <div :style="viewControlsStyle">
      <button @click="toggleAutoRotate" :style="autoRotateStyle">
        {{ autoRotate ? 'Auto \u25cf' : 'Manual \u25cb' }}
      </button>
      <div style="display:flex;gap:4px">
        <button @click="zoomIn" :style="zoomBtnStyle">+</button>
        <button @click="zoomOut" :style="zoomBtnStyle">&minus;</button>
      </div>
    </div>

    <!-- Mode toggle — bottom center -->
    <div :style="modeBarStyle">
      <button v-for="m in ['llm','human']" :key="m" @click="setMode(m)" :style="modeBtnStyle(m)">
        {{ m === 'llm' ? 'LLM, static' : 'Human, dynamic' }}
      </button>
      <button @click="showExplainer = !showExplainer" :style="explainerBtnStyle">Huh?</button>
    </div>

    <!-- Explainer overlay -->
    <div v-if="showExplainer" :style="explainerOverlayStyle">
      <div :style="explainerContentStyle">
        <div :style="explainerTitleStyle">What is this?</div>
        <p style="margin-bottom:24px">Neural Plinko, a spatial intuition</p>
        <p style="margin-bottom:16px">Imagine a Plinko board wrapped around a sphere.</p>
        <p style="margin-bottom:16px">A language model is a fixed structure, billions of learned parameters, and its output is what happens when a signal passes through that structure. This is that idea made visible.</p>
        <p style="margin-bottom:16px">The <span style="color:rgba(140,170,255,0.9)">pegs</span> are the weights. The <span style="color:rgba(100,230,210,0.9)">glowing snake</span> is the output, a trajectory shaped by the board it passes through. It's a snake rather than a ball because a model doesn't just produce one token. It leaves a trail. The tail is the context, the history of everything generated so far, still influencing what comes next.</p>
        <p style="margin-bottom:16px">Watch the pegs that <em>aren't</em> near the snake. When a weight fires, other weights in the same functional group flicker in sympathy. Trained networks develop coordinated circuits, not isolated parts. Raise <em>Training</em> to see these clusters tighten.</p>
        <p style="margin-bottom:16px">Brightness along the snake is <span style="color:rgba(100,230,210,0.9)">attention</span>. How much the model still cares about each part of its context. Not all old tokens fade equally.</p>
        <p style="margin-bottom:24px">Two modes:</p>
        <p style="margin-bottom:12px"><span style="color:rgba(140,170,255,0.9)">LLM, Static:</span> The pegs are frozen. No matter what passes through, the board stays the same. Fixed weights, only the trajectory varies.</p>
        <p style="margin-bottom:24px"><span style="color:rgba(140,170,255,0.9)">Human, Dynamic:</span> The pegs drift and shift. The board itself changes as the snake moves through it. Experience reshapes the structure, not just the output.</p>
        <p style="margin-bottom:16px">Use the <span style="color:rgba(140,170,255,0.9)">Controls</span> to change the board. Crank <em>Fidelity</em> down to see a small model with few parameters. Turn up <em>Temperature</em> and the snake starts taking less-probable paths through the same structure.</p>
        <p style="margin-top:24px;margin-bottom:16px">The spatial traversal is a concession here. You can't really visualize matrix multiplication as a physical process, so the ball-through-pegs thing is the tradeoff for making it tangible.</p>
        <p style="margin-top:16px;margin-bottom:16px;color:rgba(150,170,210,0.65)">A closer intuition, one I haven't figured out how to visualize yet, is that the sphere is actually a high-dimensional manifold, and the snake doesn't really traverse its surface. It falls <em>through</em> it. Think of the weights less as pegs that deflect and more as a medium that refracts. The signal enters, and the structure of the material bends it as it passes through, layer by layer. The refraction isn't uniform. Some regions bend the signal hard, others barely touch it. And the signal's own attention, which parts of context it's carrying forward and how strongly, changes how it interacts with each region it hits. It's not pinball. It's light through glass that's different at every point.</p>
        <p style="margin-top:32px;color:rgba(120,140,180,0.4);font-size:11px"><a href="https://mikekorostelev.com" target="_blank" rel="noopener noreferrer" style="color:rgba(140,170,255,0.5);text-decoration:none;border-bottom:1px solid rgba(140,170,255,0.2)">mikekorostelev.com</a></p>
      </div>
    </div>

    <!-- Controls toggle — top right -->
    <button @click="showPanel = !showPanel; _state && (_state.panelOpen = showPanel)" :style="controlsToggleStyle">
      {{ showPanel ? 'Hide' : 'Controls' }}
    </button>

    <!-- Controls panel -->
    <div v-if="showPanel" :style="panelStyle">
      <div>
        <div :style="sectionLabelStyle">Model</div>
        <template v-for="key in modelKeys">
          <div v-if="mode === 'human' || key !== 'dynamism'" :key="key" style="margin-bottom:16px">
            <div style="display:flex;justify-content:space-between;margin-bottom:6px">
              <button @click="activeTooltip = activeTooltip === key ? null : key" :style="sliderLabelStyle">
                {{ MODEL_PARAMS[key].label }} <span style="color:rgba(100,140,255,0.4);font-size:9px">&#9432;</span>
              </button>
              <span style="color:rgba(120,150,200,0.5);font-size:10px">{{ Math.floor(params[key] * 100) }}%</span>
            </div>
            <div v-if="activeTooltip === key" :style="tooltipStyle">{{ MODEL_PARAMS[key].desc }}</div>
            <input type="range" min="0" max="1" step="0.01" :value="params[key]" @input="updateParam(key, parseFloat($event.target.value))" />
          </div>
        </template>
      </div>
      <div style="height:12px"></div>
      <div>
        <div :style="sectionLabelStyle">Display</div>
        <div v-for="key in displayKeys" :key="key" style="margin-bottom:16px">
          <div style="display:flex;justify-content:space-between;margin-bottom:6px">
            <button @click="activeTooltip = activeTooltip === key ? null : key" :style="sliderLabelStyle">
              {{ DISPLAY_PARAMS[key].label }} <span style="color:rgba(100,140,255,0.4);font-size:9px">&#9432;</span>
            </button>
            <span style="color:rgba(120,150,200,0.5);font-size:10px">{{ Math.floor(params[key] * 100) }}%</span>
          </div>
          <div v-if="activeTooltip === key" :style="tooltipStyle">{{ DISPLAY_PARAMS[key].desc }}</div>
          <input type="range" min="0" max="1" step="0.01" :value="params[key]" @input="updateParam(key, parseFloat($event.target.value))" />
        </div>
      </div>
      <button @click="resetDefaults" :style="resetBtnStyle">Reset defaults</button>
      <div :style="legendStyle">
        <div style="margin-bottom:4px"><span style="color:rgba(140,170,255,0.7)">&bull;</span> Pegs, the board</div>
        <div style="margin-bottom:4px">
          <span style="color:rgba(18,8,35,1)">&block;</span><span style="color:rgba(15,188,170,1)"> &block;</span><span style="color:rgba(255,255,255,1)"> &block;</span> low &rarr; high attention
        </div>
        <div>Brightness = attention. Not uniform, some old context stays hot.</div>
      </div>
    </div>
  </div>
  </teleport>
</template>

<script>
const R = 5;
const MAX_PEGS = 800;

function seededRandom(seed) {
  let s = seed;
  return () => { s = (s * 16807) % 2147483647; return s / 2147483647; };
}

function generateAttractors(count, seed) {
  const rng = seededRandom(seed);
  const attractors = [];
  for (let i = 0; i < count; i++) {
    const theta = Math.acos(1 - 2 * rng());
    const phi = 2 * Math.PI * rng();
    attractors.push({
      x: R * Math.sin(theta) * Math.cos(phi),
      y: R * Math.sin(theta) * Math.sin(phi),
      z: R * Math.cos(theta),
    });
  }
  return attractors;
}

const CLUSTER_HUES = [210, 260, 170, 320, 30, 190, 280, 140, 350, 50, 230, 300, 160, 15, 240];

function generatePegs(count, training) {
  const rng = seededRandom(42);
  const pegs = [];
  const gr = (1 + Math.sqrt(5)) / 2;
  const t = training || 0;
  const attractorSeed = Math.floor(t * 1000) + 137;
  const attractors = generateAttractors(15, attractorSeed);

  for (let i = 0; i < count; i++) {
    const theta0 = Math.acos(1 - 2 * (i + 0.5) / count);
    const phi0 = (2 * Math.PI * i) / gr;
    let x = R * Math.sin(theta0) * Math.cos(phi0);
    let y = R * Math.sin(theta0) * Math.sin(phi0);
    let z = R * Math.cos(theta0);

    let nearestDist = Infinity, nearestIdx = 0;
    for (let ai = 0; ai < attractors.length; ai++) {
      const a = attractors[ai];
      const d = (x - a.x) ** 2 + (y - a.y) ** 2 + (z - a.z) ** 2;
      if (d < nearestDist) { nearestDist = d; nearestIdx = ai; }
    }

    if (t > 0.01) {
      const nearest = attractors[nearestIdx];
      const pull = t * 0.35;
      x = x * (1 - pull) + nearest.x * pull;
      y = y * (1 - pull) + nearest.y * pull;
      z = z * (1 - pull) + nearest.z * pull;
      const len = Math.sqrt(x * x + y * y + z * z);
      x = x / len * R; y = y / len * R; z = z / len * R;
    }

    const theta = Math.acos(z / R);
    const phi = Math.atan2(y, x);
    pegs.push({ theta, phi, x, y, z, influence: 0.3 + rng() * 0.7, cluster: nearestIdx });
  }
  return pegs;
}

function hsl2rgb(h, s, l) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l - c / 2;
  let r = 0, g = 0, b = 0;
  if (h < 60) { r = c; g = x; }
  else if (h < 120) { r = x; g = c; }
  else if (h < 180) { g = c; b = x; }
  else if (h < 240) { g = x; b = c; }
  else if (h < 300) { r = x; b = c; }
  else { r = c; b = x; }
  return { r: Math.round((r + m) * 255), g: Math.round((g + m) * 255), b: Math.round((b + m) * 255) };
}

function s2c(theta, phi) {
  return { x: R * Math.sin(theta) * Math.cos(phi), y: R * Math.sin(theta) * Math.sin(phi), z: R * Math.cos(theta) };
}

function slerpPt(ax, ay, az, bx, by, bz, t) {
  const al = Math.sqrt(ax * ax + ay * ay + az * az), bl = Math.sqrt(bx * bx + by * by + bz * bz);
  const nax = ax / al, nay = ay / al, naz = az / al, nbx = bx / bl, nby = by / bl, nbz = bz / bl;
  let dot = Math.max(-1, Math.min(1, nax * nbx + nay * nby + naz * nbz));
  const omega = Math.acos(dot);
  if (omega < 0.001) return { x: (nax + (nbx - nax) * t) * R, y: (nay + (nby - nay) * t) * R, z: (naz + (nbz - naz) * t) * R };
  const sinO = Math.sin(omega), sa = Math.sin((1 - t) * omega) / sinO, sb = Math.sin(t * omega) / sinO;
  return { x: (sa * nax + sb * nbx) * R, y: (sa * nay + sb * nby) * R, z: (sa * naz + sb * nbz) * R };
}

function snakeColor(attention) {
  if (attention < 0.2) {
    const p = attention / 0.2;
    return { r: 10 + p * 10 | 0, g: 40 + p * 40 | 0, b: 65 + p * 30 | 0 };
  }
  if (attention < 0.45) {
    const p = (attention - 0.2) / 0.25;
    return { r: 20 + p * 5 | 0, g: 80 + p * 50 | 0, b: 95 + p * 35 | 0 };
  }
  if (attention < 0.7) {
    const p = (attention - 0.45) / 0.25;
    return { r: 25 - p * 10 | 0, g: 130 + p * 58 | 0, b: 130 + p * 40 | 0 };
  }
  if (attention < 0.9) {
    const p = (attention - 0.7) / 0.2;
    return { r: 15 + p * 80 | 0, g: 188 + p * 50 | 0, b: 170 + p * 50 | 0 };
  }
  const p = (attention - 0.9) / 0.1;
  return { r: 95 + p * 160 | 0, g: 238 + p * 17 | 0, b: 220 + p * 35 | 0 };
}

const DEFAULTS = { weightStrength: 0.05, temperature: 0.88, modelFidelity: 0.46, modelTraining: 0.35, dynamism: 0.5, contextWindow: 0.28, recencyBias: 1.0, attentionSpikiness: 0.5, speed: 0.21, density: 0.87 };

const MODEL_PARAMS = {
  weightStrength: { label: "Confidence", desc: "Weight strength \u2014 how strongly the learned parameters deflect the trajectory. Low = weights barely matter, output drifts. High = weights dominate, output is tightly constrained." },
  temperature: { label: "Temperature", desc: "How randomly the snake samples from the weight structure. Low = deterministic. High = explores less-probable paths." },
  modelFidelity: { label: "Model Fidelity", desc: "How many pegs. More = higher-capacity model." },
  modelTraining: { label: "Model Training", desc: "How organized the pegs are. Each position is a different training run. Higher = more clustered specialization." },
  dynamism: { label: "Dynamism", desc: "How much the board drifts. Human mode only." },
};

const DISPLAY_PARAMS = {
  contextWindow: { label: "Context Window", desc: "How much history the snake carries." },
  recencyBias: { label: "Recency Bias", desc: "How much attention favors recent tokens. Low = old context stays hot. High = only recent tokens matter." },
  attentionSpikiness: { label: "Attention Spikiness", desc: "How uneven attention brightness is." },
  speed: { label: "Speed", desc: "How fast the snake moves." },
  density: { label: "Density", desc: "How tightly packed the points are." },
};

function proj(x, y, z, rx, ry, w, h, zm, xOff) {
  const x1 = x * Math.cos(ry) - z * Math.sin(ry), z1 = x * Math.sin(ry) + z * Math.cos(ry);
  const y1 = y * Math.cos(rx) - z1 * Math.sin(rx), z2 = y * Math.sin(rx) + z1 * Math.cos(rx);
  const sc = 18 / (18 + z2);
  const zf = zm || 1;
  const ox = xOff || 0;
  return { sx: w / 2 + ox + x1 * sc * w / 8 * zf, sy: h / 2 + y1 * sc * w / 8 * zf, scale: sc * zf, z: z2 };
}

export default {
  name: 'NeuralPlinko',
  data() {
    return {
      mode: 'llm',
      params: { ...DEFAULTS },
      showPanel: false,
      activeTooltip: null,
      autoRotate: true,
      showExplainer: false,
      MODEL_PARAMS,
      DISPLAY_PARAMS,
      modelKeys: Object.keys(MODEL_PARAMS),
      displayKeys: Object.keys(DISPLAY_PARAMS),
    };
  },
  computed: {
    containerStyle() {
      return { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%', background: '#0a0a0f', overflow: 'hidden', zIndex: 9999, fontFamily: "'JetBrains Mono','SF Mono','Fira Code',monospace" };
    },
    canvasStyle() {
      return { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'block', cursor: 'grab' };
    },
    backBtnStyle() {
      return { position: 'absolute', top: '12px', left: '12px', color: 'rgba(180,200,255,0.6)', fontSize: '11px', letterSpacing: '0.08em', textDecoration: 'none', cursor: 'pointer', zIndex: 30, padding: '6px 12px', background: 'rgba(100,140,255,0.08)', border: '1px solid rgba(100,140,255,0.2)', borderRadius: '6px' };
    },
    titleStyle() {
      return { position: 'absolute', top: '48px', left: '12px', color: 'rgba(180,200,255,0.8)', fontSize: '13px', letterSpacing: '0.15em', textTransform: 'uppercase', zIndex: 30 };
    },
    viewControlsStyle() {
      return { position: 'absolute', top: '76px', left: '12px', display: 'flex', flexDirection: 'column', gap: '6px' };
    },
    autoRotateStyle() {
      return {
        background: this.autoRotate ? 'rgba(100,140,255,0.15)' : 'rgba(255,140,100,0.12)',
        border: `1px solid ${this.autoRotate ? 'rgba(100,140,255,0.25)' : 'rgba(255,140,100,0.25)'}`,
        borderRadius: '6px', padding: '6px 10px',
        color: this.autoRotate ? 'rgba(180,210,255,0.8)' : 'rgba(255,190,150,0.8)',
        fontSize: '9px', letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.3s',
      };
    },
    zoomBtnStyle() {
      return {
        background: 'rgba(100,140,255,0.08)', border: '1px solid rgba(100,140,255,0.2)',
        borderRadius: '5px', width: '32px', height: '32px', color: 'rgba(180,200,255,0.7)',
        fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
      };
    },
    modeBarStyle() {
      return { position: 'absolute', bottom: '20px', left: '50%', transform: 'translateX(-50%)', display: 'flex', border: '1px solid rgba(100,140,255,0.25)', borderRadius: '6px', overflow: 'hidden', zIndex: 20 };
    },
    explainerBtnStyle() {
      return {
        padding: '8px 16px', border: 'none', borderLeft: '1px solid rgba(100,140,255,0.25)',
        fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.3s',
        background: this.showExplainer ? 'rgba(100,140,255,0.15)' : 'transparent',
        color: this.showExplainer ? 'rgba(180,210,255,0.9)' : 'rgba(120,140,180,0.5)',
      };
    },
    explainerOverlayStyle() {
      return { position: 'absolute', top: 0, left: 0, right: 0, bottom: '65px', background: 'rgba(8,8,18,0.90)', zIndex: 10, display: 'flex', justifyContent: 'center', overflowY: 'auto', padding: '40px 40px 20px 40px' };
    },
    explainerContentStyle() {
      return { maxWidth: '520px', color: 'rgba(180,200,240,0.85)', fontSize: '14px', lineHeight: 1.8, letterSpacing: '0.01em', paddingTop: '20px' };
    },
    explainerTitleStyle() {
      return { fontSize: '18px', color: 'rgba(180,210,255,0.9)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' };
    },
    controlsToggleStyle() {
      return {
        position: 'absolute', top: '12px', right: '12px',
        background: this.showPanel ? 'rgba(100,140,255,0.2)' : 'rgba(100,140,255,0.08)',
        border: '1px solid rgba(100,140,255,0.25)', borderRadius: '6px', padding: '6px 12px',
        color: 'rgba(180,200,255,0.8)', fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer',
      };
    },
    panelStyle() {
      return {
        position: 'absolute', top: '44px', right: '12px', width: '260px', padding: '16px',
        background: 'rgba(8,8,18,0.92)', border: '1px solid rgba(100,140,255,0.15)', borderRadius: '10px',
        backdropFilter: 'blur(12px)', maxHeight: 'calc(100vh - 120px)', overflowY: 'auto',
      };
    },
    sectionLabelStyle() {
      return { color: 'rgba(100,140,255,0.5)', fontSize: '8px', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '10px', borderBottom: '1px solid rgba(100,140,255,0.1)', paddingBottom: '6px' };
    },
    sliderLabelStyle() {
      return { background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'rgba(160,185,230,0.85)', fontSize: '10px', letterSpacing: '0.08em', textTransform: 'uppercase', textAlign: 'left' };
    },
    tooltipStyle() {
      return { color: 'rgba(130,150,190,0.6)', fontSize: '9px', lineHeight: 1.5, marginBottom: '6px' };
    },
    resetBtnStyle() {
      return {
        width: '100%', padding: '8px 0', marginTop: '10px', background: 'rgba(100,140,255,0.08)',
        border: '1px solid rgba(100,140,255,0.15)', borderRadius: '5px', color: 'rgba(140,160,210,0.5)',
        fontSize: '9px', letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer',
      };
    },
    legendStyle() {
      return { marginTop: '16px', paddingTop: '12px', borderTop: '1px solid rgba(100,140,255,0.1)', color: 'rgba(140,160,200,0.5)', fontSize: '9px' };
    },
  },
  mounted() {
    document.body.classList.add('neural-plinko-active');
    this.initCanvas();
  },
  beforeUnmount() {
    document.body.classList.remove('neural-plinko-active');
    if (this._animId) cancelAnimationFrame(this._animId);
    if (this._cleanup) this._cleanup();
  },
  methods: {
    modeBtnStyle(m) {
      const active = !this.showExplainer && this.mode === m;
      return {
        padding: '8px 16px', border: 'none', fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.3s',
        background: active ? 'rgba(100,140,255,0.15)' : 'transparent',
        color: active ? 'rgba(180,210,255,0.9)' : 'rgba(120,140,180,0.5)',
      };
    },
    updateParam(key, val) {
      this.params[key] = val;
      this._state.params[key] = val;
    },
    resetDefaults() {
      Object.assign(this.params, DEFAULTS);
      Object.assign(this._state.params, DEFAULTS);
    },
    toggleAutoRotate() {
      this.autoRotate = !this.autoRotate;
      this._state.autoRotate = this.autoRotate;
    },
    setMode(m) {
      this.mode = m;
      this._state.mode = m;
      this.showExplainer = false;
    },
    zoomIn() { this._state.zoom = Math.min(5, this._state.zoom * 1.3); },
    zoomOut() { this._state.zoom = Math.max(0.3, this._state.zoom / 1.3); },

    initCanvas() {
      const canvas = this.$refs.canvasEl;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');

      const resize = () => { canvas.width = canvas.offsetWidth * 2; canvas.height = canvas.offsetHeight * 2; };
      resize();
      window.addEventListener('resize', resize);

      const s = {
        pegs: generatePegs(Math.floor(10 + DEFAULTS.modelFidelity * (MAX_PEGS - 10)), Math.min(0.6, DEFAULTS.modelTraining)),
        snake: [],
        theta: Math.PI * 0.5, phi: 0, heading: 0.7,
        rotation: { x: 0.3, y: 0 }, zoom: 0.5, time: 0,
        isDragging: false, lastMouse: { x: 0, y: 0 }, lastPinchDist: 0, isPinching: false, autoRotate: true,
        pegJitter: new Array(MAX_PEGS).fill(0).map(() => ({ dx: 0, dy: 0, dz: 0 })),
        pegGlow: new Array(MAX_PEGS).fill(0),
        pegSympathy: new Array(MAX_PEGS).fill(0),
        lastPegCount: Math.floor(10 + DEFAULTS.modelFidelity * (MAX_PEGS - 10)),
        lastTrainingKey: Math.round(Math.min(0.6, DEFAULTS.modelTraining) * 100),
        perplexity: 0, smoothPerplexity: 5,
        mode: 'llm', params: { ...DEFAULTS }, panelOpen: false,
      };
      this._state = s;

      const onDown = (e) => {
        if (e.target !== canvas) return;
        if (e.touches && e.touches.length === 2) {
          s.isDragging = false; s.isPinching = true;
          const dx = e.touches[0].clientX - e.touches[1].clientX;
          const dy = e.touches[0].clientY - e.touches[1].clientY;
          s.lastPinchDist = Math.sqrt(dx * dx + dy * dy);
          return;
        }
        if (e.touches && e.touches.length > 2) return;
        s.isDragging = true; s.isPinching = false;
        const p = e.touches ? e.touches[0] : e;
        s.lastMouse = { x: p.clientX, y: p.clientY };
      };
      const onMove = (e) => {
        if (e.touches && e.touches.length === 2) {
          e.preventDefault();
          s.isPinching = true; s.isDragging = false;
          const dx = e.touches[0].clientX - e.touches[1].clientX;
          const dy = e.touches[0].clientY - e.touches[1].clientY;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (s.lastPinchDist > 0) {
            s.zoom = Math.max(0.3, Math.min(5.0, s.zoom * (dist / s.lastPinchDist)));
          }
          s.lastPinchDist = dist;
          return;
        }
        if (s.isPinching) return;
        if (!s.isDragging) return;
        const p = e.touches ? e.touches[0] : e;
        s.rotation.y += (p.clientX - s.lastMouse.x) * 0.008;
        s.rotation.x = Math.max(-1.4, Math.min(1.4, s.rotation.x + (p.clientY - s.lastMouse.y) * 0.008));
        s.lastMouse = { x: p.clientX, y: p.clientY };
      };
      const onUp = (e) => {
        if (e.touches && e.touches.length > 0) return;
        s.isDragging = false; s.isPinching = false; s.lastPinchDist = 0;
      };
      const onWheel = (e) => {
        e.preventDefault();
        s.zoom = Math.max(0.3, Math.min(5.0, s.zoom * (1 - e.deltaY * 0.001)));
      };

      canvas.addEventListener('mousedown', onDown);
      canvas.addEventListener('mousemove', onMove);
      canvas.addEventListener('mouseup', onUp);
      canvas.addEventListener('mouseleave', onUp);
      canvas.addEventListener('touchstart', onDown, { passive: false });
      canvas.addEventListener('touchmove', onMove, { passive: false });
      canvas.addEventListener('touchend', onUp);
      canvas.addEventListener('wheel', onWheel, { passive: false });

      const animate = () => {
        const P = s.params;
        const w = canvas.width, h = canvas.height;
        s.time++;
        if (s.autoRotate && !s.isDragging) s.rotation.y += 0.002;
        const rx = s.rotation.x, ry = s.rotation.y, zm = s.zoom;
        const xOff = s.panelOpen ? -280 : 0;

        const maxLen = Math.floor(80 + P.contextWindow * 2500);
        const stepSize = 0.025 - P.density * 0.02;
        const stepsPerFrame = Math.max(1, Math.round(1 + P.speed * 8));
        const pegStr = 0.5 + P.weightStrength * 3.0;
        const activePegs = Math.max(10, Math.floor(10 + P.modelFidelity * (MAX_PEGS - 10)));
        const training = Math.min(0.6, P.modelTraining || 0);
        const jitterAmt = P.dynamism * 0.2;

        const trainingKey = Math.round(training * 100);
        if (activePegs !== s.lastPegCount || trainingKey !== s.lastTrainingKey) {
          s.pegs = generatePegs(activePegs, training);
          s.pegGlow = new Array(MAX_PEGS).fill(0);
          s.pegSympathy = new Array(MAX_PEGS).fill(0);
          s.lastPegCount = activePegs;
          s.lastTrainingKey = trainingKey;
        }

        if (s.mode === 'human') {
          for (let i = 0; i < activePegs; i++) {
            const t = s.time * 0.02 + i * 0.5;
            s.pegJitter[i] = { dx: Math.sin(t * 1.3) * jitterAmt, dy: Math.cos(t * 0.9) * jitterAmt, dz: Math.sin(t * 1.7 + 2) * jitterAmt };
          }
        } else {
          for (const j of s.pegJitter) { j.dx *= 0.95; j.dy *= 0.95; j.dz *= 0.95; }
        }

        let framePegMag = 0;
        let frameRawPegMag = 0;
        for (let step = 0; step < stepsPerFrame; step++) {
          const sinT = Math.sin(s.theta), cosT = Math.cos(s.theta);
          const sinP = Math.sin(s.phi), cosP = Math.cos(s.phi);
          const pos = s2c(s.theta, s.phi);
          const etx = cosT * cosP, ety = cosT * sinP, etz = -sinT;
          const epx = -sinP, epy = cosP, epz = 0;

          let steer = 0;
          let pegSteerMag = 0;
          for (let pi = 0; pi < activePegs; pi++) {
            const peg = s.pegs[pi];
            const dx = pos.x - peg.x, dy = pos.y - peg.y, dz = pos.z - peg.z;
            const distSq = dx * dx + dy * dy + dz * dz;
            if (distSq < 5.0 && distSq > 0.01) {
              const dist = Math.sqrt(distSq);
              const projT = dx * etx + dy * ety + dz * etz;
              const projP = dx * epx + dy * epy + dz * epz;
              const repelAngle = Math.atan2(projP, projT);
              let angleDiff = repelAngle - s.heading;
              while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
              while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;
              const force = (peg.influence / (dist * dist)) * pegStr;
              const contribution = angleDiff * force * 0.06;
              steer += contribution;
              pegSteerMag += Math.abs(contribution);
            }
          }

          const maxSteer = 0.25;
          if (Math.abs(steer) > maxSteer) steer = Math.sign(steer) * maxSteer;
          frameRawPegMag += pegSteerMag;
          pegSteerMag = Math.min(pegSteerMag, maxSteer * 2);
          framePegMag += pegSteerMag;

          steer += (Math.random() - 0.5) * pegSteerMag * P.temperature * 1.5;
          steer += Math.sin(s.time * 0.005 + step * 0.1) * 0.0005;
          s.heading += steer;

          const dTheta = Math.cos(s.heading) * stepSize;
          const dPhi = Math.sin(s.heading) * stepSize / Math.max(Math.sin(s.theta), 0.15);
          s.theta += dTheta;
          s.phi += dPhi;

          if (s.theta < 0.08) { s.theta = 0.16 - s.theta; s.heading = Math.PI - s.heading; }
          if (s.theta > Math.PI - 0.08) { s.theta = 2 * (Math.PI - 0.08) - s.theta; s.heading = Math.PI - s.heading; }

          s.snake.push({ ...s2c(s.theta, s.phi), attn: Math.random() });
          while (s.snake.length > maxLen) s.snake.shift();
        }

        const avgRawPegMag = frameRawPegMag / Math.max(1, stepsPerFrame);
        const influence = Math.min(1, avgRawPegMag * 0.8);
        const rawPerplexity = 1 + (1 - influence) * (1 - influence) * 9;
        s.smoothPerplexity = s.smoothPerplexity * 0.85 + rawPerplexity * 0.15;

        for (let i = 0; i < activePegs; i++) {
          s.pegGlow[i] *= 0.985;
          s.pegSympathy[i] *= 0.94;
        }
        const startIdx = Math.max(0, s.snake.length - stepsPerFrame);
        for (let si = startIdx; si < s.snake.length; si++) {
          const sp = s.snake[si];
          for (let i = 0; i < activePegs; i++) {
            const peg = s.pegs[i];
            const dx = sp.x - peg.x, dy = sp.y - peg.y, dz = sp.z - peg.z;
            const distSq = dx * dx + dy * dy + dz * dz;
            if (distSq < 6.0) {
              const proximity = 1 - Math.sqrt(distSq) / Math.sqrt(6.0);
              s.pegGlow[i] = Math.min(1, Math.max(s.pegGlow[i], proximity));
            }
          }
        }

        const sympathyStrength = 0.05 + training * 0.55;
        for (let i = 0; i < activePegs; i++) {
          if (s.pegGlow[i] > 0.3) {
            const srcCluster = s.pegs[i].cluster;
            const srcGlow = s.pegGlow[i];
            for (let j = 0; j < activePegs; j++) {
              if (i === j) continue;
              if (s.pegs[j].cluster !== srcCluster) continue;
              if (((i * 7 + j * 13 + s.time) % 5) > 1) continue;
              const pulse = srcGlow * sympathyStrength * 0.4;
              s.pegSympathy[j] = Math.min(0.6, Math.max(s.pegSympathy[j], pulse));
            }
          }
        }

        // === DRAW ===
        ctx.fillStyle = '#0a0a0f';
        ctx.fillRect(0, 0, w, h);

        // Wireframe
        ctx.lineWidth = 1;
        for (let lat = 1; lat < 6; lat++) {
          const tv = (lat / 6) * Math.PI;
          ctx.beginPath();
          for (let i = 0; i <= 64; i++) {
            const pv = (i / 64) * Math.PI * 2, pt = s2c(tv, pv), p = proj(pt.x, pt.y, pt.z, rx, ry, w, h, zm, xOff);
            ctx.strokeStyle = `rgba(40,40,65,${Math.max(0.04, Math.min(0.22, (p.z + 6) / 14))})`;
            i === 0 ? ctx.moveTo(p.sx, p.sy) : ctx.lineTo(p.sx, p.sy);
          }
          ctx.stroke();
        }
        for (let lon = 0; lon < 8; lon++) {
          const pv = (lon / 8) * Math.PI * 2;
          ctx.beginPath();
          for (let i = 0; i <= 32; i++) {
            const tv = (i / 32) * Math.PI, pt = s2c(tv, pv), p = proj(pt.x, pt.y, pt.z, rx, ry, w, h, zm, xOff);
            ctx.strokeStyle = `rgba(40,40,65,${Math.max(0.04, Math.min(0.22, (p.z + 6) / 14))})`;
            i === 0 ? ctx.moveTo(p.sx, p.sy) : ctx.lineTo(p.sx, p.sy);
          }
          ctx.stroke();
        }

        // Collect and sort by depth
        const draws = [];
        for (let i = 0; i < activePegs; i++) {
          const pg = s.pegs[i], j = s.pegJitter[i];
          draws.push({ k: 'p', p: proj(pg.x + j.dx, pg.y + j.dy, pg.z + j.dz, rx, ry, w, h, zm, xOff), inf: pg.influence, glow: s.pegGlow[i], sympathy: s.pegSympathy[i], cluster: pg.cluster });
        }
        for (let i = 0; i < s.snake.length; i++) {
          const sg = s.snake[i];
          draws.push({ k: 's', p: proj(sg.x, sg.y, sg.z, rx, ry, w, h, zm, xOff), i, n: s.snake.length, attn: sg.attn });
        }
        draws.sort((a, b) => b.p.z - a.p.z);

        for (const d of draws) {
          const al = Math.max(0.04, Math.min(0.9, (d.p.z + 6) / 12));
          if (d.k === 'p') {
            const gw = d.glow || 0;
            const sym = d.sympathy || 0;
            const combined = Math.min(1, gw + sym * 0.5);
            const baseSz = Math.max(1.5, 3 * d.p.scale * (0.5 + d.inf * 0.5));
            const sz = baseSz * (1 + gw * 2.25 + sym * 0.6);

            const clusterHue = CLUSTER_HUES[d.cluster % CLUSTER_HUES.length];
            const tintStrength = training * 0.45 + combined * 0.25;
            const baseColor = hsl2rgb(clusterHue, 0.4 + combined * 0.3, 0.45 + combined * 0.2);
            const gr = Math.floor(100 + combined * 41 + (baseColor.r - 100) * tintStrength);
            const gg = Math.floor(140 + combined * 86 + (baseColor.g - 140) * tintStrength);
            const gb = Math.floor(255 + (baseColor.b - 255) * tintStrength * 0.5);

            if (gw > 0.05) {
              const glowR = sz * (4 + gw * 9);
              const g = ctx.createRadialGradient(d.p.sx, d.p.sy, 0, d.p.sx, d.p.sy, glowR);
              g.addColorStop(0, `rgba(${gr},${gg},${gb},${al * gw * 0.45})`);
              g.addColorStop(0.4, `rgba(${gr},${gg},${gb},${al * gw * 0.11})`);
              g.addColorStop(1, `rgba(${gr},${gg},${gb},0)`);
              ctx.fillStyle = g; ctx.beginPath(); ctx.arc(d.p.sx, d.p.sy, glowR, 0, Math.PI * 2); ctx.fill();
            }
            if (gw <= 0.05 && sym > 0.05) {
              const symR = baseSz * (2.5 + sym * 5);
              const g = ctx.createRadialGradient(d.p.sx, d.p.sy, 0, d.p.sx, d.p.sy, symR);
              g.addColorStop(0, `rgba(${gr},${gg},${gb},${al * sym * 0.25})`);
              g.addColorStop(1, `rgba(${gr},${gg},${gb},0)`);
              ctx.fillStyle = g; ctx.beginPath(); ctx.arc(d.p.sx, d.p.sy, symR, 0, Math.PI * 2); ctx.fill();
            }
            if (gw <= 0.05 && sym <= 0.05) {
              const g = ctx.createRadialGradient(d.p.sx, d.p.sy, 0, d.p.sx, d.p.sy, baseSz * 3.5);
              g.addColorStop(0, `rgba(${gr},${gg},${gb},${al * 0.1})`);
              g.addColorStop(1, `rgba(${gr},${gg},${gb},0)`);
              ctx.fillStyle = g; ctx.beginPath(); ctx.arc(d.p.sx, d.p.sy, baseSz * 3.5, 0, Math.PI * 2); ctx.fill();
            }
            ctx.fillStyle = `rgba(${gr},${gg},${gb},${al * (0.6 + combined * 0.3)})`;
            ctx.beginPath(); ctx.arc(d.p.sx, d.p.sy, sz, 0, Math.PI * 2); ctx.fill();
          } else {
            const recency = d.i / d.n;
            const attnBase = d.attn || 0.5;
            const falloff = P.recencyBias;
            const spikiness = P.attentionSpikiness;
            const floor = Math.pow(recency, 0.3 + falloff * 2.5) * 0.45;
            const sharpened = Math.pow(attnBase, 1.0 + (1.0 - spikiness) * 3);
            const spike = sharpened * (0.25 + recency * 0.35) * (0.4 + spikiness * 1.2);
            const attention = Math.min(1, floor + spike);

            const { r, g, b } = snakeColor(attention);
            const th = (1.0 + attention * 8) * d.p.scale;
            if (attention > 0.35) {
              const gs = th * (2 + attention * 7);
              const gl = ctx.createRadialGradient(d.p.sx, d.p.sy, 0, d.p.sx, d.p.sy, gs);
              gl.addColorStop(0, `rgba(${r},${g},${b},${al * attention * 0.4})`);
              gl.addColorStop(1, `rgba(${r},${g},${b},0)`);
              ctx.fillStyle = gl; ctx.beginPath(); ctx.arc(d.p.sx, d.p.sy, gs, 0, Math.PI * 2); ctx.fill();
            }
            ctx.fillStyle = `rgba(${r},${g},${b},${al * (0.1 + attention * 0.9)})`;
            ctx.beginPath(); ctx.arc(d.p.sx, d.p.sy, th, 0, Math.PI * 2); ctx.fill();
          }
        }

        // Snake arc connections
        if (s.snake.length > 1) {
          for (let i = 1; i < s.snake.length; i++) {
            const a = s.snake[i - 1], b = s.snake[i];
            const recency = i / s.snake.length;
            const attnBase = (a.attn + b.attn) / 2;
            const falloff = P.recencyBias;
            const spikiness = P.attentionSpikiness;
            const floor = Math.pow(recency, 0.3 + falloff * 2.5) * 0.45;
            const sharpened = Math.pow(attnBase, 1.0 + (1.0 - spikiness) * 3);
            const spike = sharpened * (0.25 + recency * 0.35) * (0.4 + spikiness * 1.2);
            const attention = Math.min(1, floor + spike);

            const { r, g, b: bl } = snakeColor(attention);
            ctx.beginPath();
            for (let st = 0; st <= 3; st++) {
              const pt = slerpPt(a.x, a.y, a.z, b.x, b.y, b.z, st / 3);
              const p = proj(pt.x, pt.y, pt.z, rx, ry, w, h, zm, xOff);
              st === 0 ? ctx.moveTo(p.sx, p.sy) : ctx.lineTo(p.sx, p.sy);
            }
            const p1 = proj(a.x, a.y, a.z, rx, ry, w, h, zm, xOff), p2 = proj(b.x, b.y, b.z, rx, ry, w, h, zm, xOff);
            const aa = Math.max(0.02, Math.min(0.8, ((p1.z + p2.z) / 2 + 6) / 12));
            ctx.strokeStyle = `rgba(${r},${g},${bl},${aa * (0.08 + attention * 0.8)})`;
            ctx.lineWidth = Math.max(0.5, (0.5 + attention * 7) * (p1.scale + p2.scale) / 2);
            ctx.stroke();
          }
        }

        // Head glow
        if (s.snake.length > 0) {
          const hd = s.snake[s.snake.length - 1], hp = proj(hd.x, hd.y, hd.z, rx, ry, w, h, zm, xOff);
          const ha = Math.max(0.06, Math.min(0.55, (hp.z + 6) / 12));
          const g = ctx.createRadialGradient(hp.sx, hp.sy, 0, hp.sx, hp.sy, 20 * hp.scale);
          g.addColorStop(0, `rgba(255,255,255,${ha})`);
          g.addColorStop(0.3, `rgba(150,230,255,${ha * 0.4})`);
          g.addColorStop(1, 'rgba(100,200,255,0)');
          ctx.fillStyle = g; ctx.beginPath(); ctx.arc(hp.sx, hp.sy, 20 * hp.scale, 0, Math.PI * 2); ctx.fill();
        }

        // Perplexity readout
        const pp = s.smoothPerplexity;
        const ppBright = Math.min(1, (pp - 1) / 9);
        ctx.font = "24px 'JetBrains Mono','SF Mono','Fira Code',monospace";
        ctx.fillStyle = `rgba(${100 + ppBright * 155},${150 + ppBright * 50},${200 + ppBright * 55},${0.25 + ppBright * 0.55})`;
        ctx.fillText(`\u27C2 ${pp.toFixed(1)}`, 44, h - 56);
        ctx.font = "13px 'JetBrains Mono','SF Mono','Fira Code',monospace";
        ctx.fillStyle = `rgba(130,150,200,${0.15 + ppBright * 0.25})`;
        ctx.fillText('perplexity', 44, h - 34);

        this._animId = requestAnimationFrame(animate);
      };

      this._animId = requestAnimationFrame(animate);

      this._cleanup = () => {
        window.removeEventListener('resize', resize);
        canvas.removeEventListener('mousedown', onDown);
        canvas.removeEventListener('mousemove', onMove);
        canvas.removeEventListener('mouseup', onUp);
        canvas.removeEventListener('mouseleave', onUp);
        canvas.removeEventListener('touchstart', onDown);
        canvas.removeEventListener('touchmove', onMove);
        canvas.removeEventListener('touchend', onUp);
        canvas.removeEventListener('wheel', onWheel);
      };
    },
  },
};
</script>

<style>
body.neural-plinko-active {
  overflow: hidden !important;
  margin: 0 !important;
  padding: 0 !important;
}
body.neural-plinko-active #app {
  display: none !important;
}
input[type=range] {
  -webkit-appearance: none;
  width: 100%;
  height: 4px;
  background: rgba(60,80,140,0.3);
  border-radius: 2px;
  outline: none;
}
input[type=range]::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  background: rgba(140,170,255,0.9);
  border-radius: 50%;
  border: 2px solid rgba(100,140,255,0.4);
  cursor: pointer;
}
</style>
